import { useEffect, useMemo, useState } from 'react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { colors } from '../lib/colors';
import type { CaseStudy } from '../utils/caseStudyTypes';
import { getAllCaseStudies } from '../utils/caseStudyLoader';

type SegmentConfig = {
  id: string;
  label: string;
  description: string;
  highlights: string[];
  primaryCTA: string;
  ctaHref?: string;
  placeholder?: {
    headline: string;
    bullets: string[];
    ctaText?: string;
  };
};

const SEGMENT_CONFIG: SegmentConfig[] = [
  {
    id: 'All',
    label: 'All Segments',
    description:
      'Browse flagship PCM case studies across high-net-worth families, business owners, incorporated professionals, and everyday households.',
    highlights: [
      'See how insurance-based strategies unlock liquidity, protect income, and build retirement cash flow.',
      'Each story is anonymized, grounded in real numbers, and tailored to prairie market realities.',
      'Filter by audience anytime to drill into the scenarios that match your situation.',
    ],
    primaryCTA: 'Talk with an Advisor',
    ctaHref: '/contact',
  },
  {
    id: 'High-Net-Worth Individuals',
    label: 'High-Net-Worth Families',
    description:
      'When corporate wealth, farmland, or layered holdings anchor the family balance sheet, liquidity and fairness drive every decision.',
    highlights: [
      'Unlock capital tied inside companies without triggering forced sales.',
      'Balance inheritances while covering estate tax obligations.',
      'Coordinate banking, insurance, and CDA planning with your advisory team.',
    ],
    primaryCTA: 'Plan your liquidity strategy',
    ctaHref: '/contact?topic=liquidity',
  },
  {
    id: 'Business Owners',
    label: 'Growth-Minded Business Owners',
    description:
      'Privately held companies need contingency plans that protect valuations, keep lenders confident, and support succession or M&A timelines.',
    highlights: [
      'Fund buy-sell agreements without draining operating cash.',
      'Cover key leader risk during expansion or acquisition.',
      'Align insurance structures with lending covenants and governance.',
    ],
    primaryCTA: 'Protect your business transition',
    ctaHref: '/contact?topic=business-succession',
  },
  {
    id: 'Professionals',
    label: 'Incorporated Professionals',
    description:
      'Retained earnings and surplus cash should fuel retirement income while preserving estate goals for physicians, accountants, and specialists.',
    highlights: [
      'Convert corporate surplus into tax-advantaged assets.',
      'Pre-approve retirement lending facilities tied to policy values.',
      'Keep legal and tax teams aligned on policy ownership and lending.',
    ],
    primaryCTA: 'Design your retirement income plan',
    ctaHref: '/contact?topic=professional-planning',
  },
  {
    id: 'Everyday Families',
    label: 'Everyday Families',
    description:
      'Dual-income households building wealth need flexible protection, savings, and education strategies that adapt as life changes.',
    highlights: [
      'Blend insurance with savings to guard against income shocks.',
      'Structure RESP and TFSA contributions alongside protection needs.',
      'Plan for mortgage coverage, education funding, and future liquidity.',
    ],
    primaryCTA: 'Build your family protection plan',
    ctaHref: '/contact?topic=family-planning',
    placeholder: {
      headline: 'Family scenarios in progress',
      bullets: [
        'We’re drafting composite stories that show how protection and savings plans work across school, mortgage, and career milestones.',
        'Talk with us now to model your family’s roadmap while we finalize these examples.',
      ],
      ctaText: 'Preview your family plan',
    },
  },
  {
    id: 'Retirement Planning',
    label: 'Retirement Planning',
    description:
      'Near-retirees and recent retirees want predictable income without eroding estates or triggering surprise tax.',
    highlights: [
      'Map corporate or personal assets into dependable income streams.',
      'Stress-test income against rate changes and market shocks.',
      'Protect legacy goals while drawing the cash flow you need.',
    ],
    primaryCTA: 'Map your retirement income',
    ctaHref: '/contact?topic=retirement',
    placeholder: {
      headline: 'Retirement income case studies coming soon',
      bullets: [
        'We’re preparing stories that show how corporate IRPs and personal pensions can work together.',
        'Book time with an advisor to run the numbers for your retirement objectives today.',
      ],
      ctaText: 'Schedule a retirement consult',
    },
  },
];

const serviceLabelMap: Record<string, string> = {
  'immediate-financing-arrangement': 'Immediate Financing Arrangement',
  'estate-preservation': 'Estate Preservation',
  'business-succession': 'Business Succession',
  'key-person-insurance': 'Key Person Insurance',
  'buy-sell-agreement-funding': 'Buy-Sell Agreement Funding',
  'corporate-owned-life-insurance': 'Corporate-Owned Life Insurance',
  'insured-retirement-strategy': 'Insured Retirement Strategy',
  'tax-planning': 'Tax Planning',
};

function formatServiceLabel(value: string) {
  return serviceLabelMap[value] || value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function formatDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
}

function getFirstSentence(text: string, maxLength = 180) {
  if (!text) return '';
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLength) {
    return cleaned;
  }
  const sentenceEnd = cleaned.indexOf('. ');
  if (sentenceEnd > 0 && sentenceEnd < maxLength) {
    return `${cleaned.slice(0, sentenceEnd + 1)}`.trim();
  }
  return `${cleaned.slice(0, maxLength).trim()}…`;
}

export default function CaseStudies() {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>(SEGMENT_CONFIG[0]?.id ?? '');

  useEffect(() => {
    getAllCaseStudies()
      .then((data) => {
        setCaseStudies(data);
        setError(data.length === 0 ? 'No case studies are available yet. Check back soon.' : null);

        if (typeof window !== 'undefined') {
          const hash = window.location.hash.replace('#', '');
          if (hash && data.some((study) => study.slug === hash)) {
            const matchedStudy = data.find((study) => study.slug === hash);
            if (matchedStudy) {
              const matchedSegment = SEGMENT_CONFIG.find((segment) =>
                matchedStudy.segments?.includes(segment.id)
              );
              if (matchedSegment) {
                setSelectedSegmentId(matchedSegment.id);
              }
            }
            setExpandedSlug(hash);
            setTimeout(() => {
              const target = document.getElementById(hash);
              if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }
        }
      })
      .catch(() => {
        setError('We were unable to load case studies. Please refresh or contact the PCM team.');
      })
      .finally(() => setLoading(false));
  }, []);

  const sectionStyle = useMemo(() => ({
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)'
  }), []);

  const segments = SEGMENT_CONFIG;
  const effectiveSegmentId = selectedSegmentId || segments[0]?.id || '';
  const activeSegment =
    segments.find((segment) => segment.id === effectiveSegmentId) ?? segments[0];

  const filteredStudies = useMemo(() => {
    if (!effectiveSegmentId) {
      return [];
    }
    if (effectiveSegmentId === 'All') {
      return caseStudies;
    }
    return caseStudies.filter((study) => study.segments?.includes(effectiveSegmentId));
  }, [caseStudies, effectiveSegmentId]);

  return (
    <MarketingLayout>
      <section className="min-h-screen py-16" style={sectionStyle}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-16 text-gray-900">
            <p className="text-sm font-semibold tracking-wide text-blue-700/70 mb-2">Client Outcomes</p>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Case Studies</h1>
            <p className="max-w-3xl text-base md:text-lg text-gray-600 leading-7">
              Explore how PCM equips prairie entrepreneurs, professionals, and farm families with advanced insurance strategies.
              Use the audience filter to jump straight to the stories that mirror your situation—each highlights the challenge, the PCM solution, and the measured result.
            </p>
            <div className="mt-8 rounded-3xl border border-gray-200 bg-white shadow-sm">
              <div className="grid gap-6 p-6 md:p-8 md:grid-cols-3">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-blue-700/70">Step 1</p>
                  <h2 className="text-lg font-semibold text-gray-900">Choose your lens</h2>
                  <p className="text-sm text-gray-600 leading-6">
                    Use the tabs to jump between high-net-worth families, business owners, professionals, everyday households,
                    and retirement-income planners.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-blue-700/70">Step 2</p>
                  <h2 className="text-lg font-semibold text-gray-900">Scan the blueprint</h2>
                  <p className="text-sm text-gray-600 leading-6">
                    Every story spells out the client’s challenge, the PCM solution, and the measured result so you can see how the
                    strategy actually plays out.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-blue-700/70">Step 3</p>
                  <h2 className="text-lg font-semibold text-gray-900">Start a tailored plan</h2>
                  <p className="text-sm text-gray-600 leading-6">
                    When a scenario mirrors yours, use the segment CTA to talk with our advisors about calibrating the approach to your
                    numbers and timelines.
                  </p>
                </div>
              </div>
            </div>
          </header>

          {loading && (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-600 shadow-sm">
              Loading case studies…
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700 shadow-sm">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="mb-10 flex flex-wrap gap-3">
                {segments.map((segment) => {
                  const isActive = effectiveSegmentId === segment.id;
                  return (
                    <button
                      key={segment.id}
                      type="button"
                      onClick={() => setSelectedSegmentId(segment.id)}
                      className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {segment.label}
                    </button>
                  );
                })}
              </div>

              <div className="mb-10 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-blue-700/70 mb-2">Segment spotlight</p>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">{activeSegment.label}</h2>
                <p className="text-sm text-gray-600 leading-7 mb-4">{activeSegment.description}</p>
                <ul className="space-y-2 text-sm text-gray-700 leading-6">
                  {activeSegment.highlights.map((item, index) => (
                    <li key={`${activeSegment.id}-highlight-${index}`} className="flex items-start gap-2">
                      <span className="mt-1 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {filteredStudies.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-600 shadow-sm">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {activeSegment.placeholder?.headline || 'Case studies coming soon'}
                  </h3>
                  <ul className="space-y-2 text-sm text-gray-600 mb-6">
                    {(activeSegment.placeholder?.bullets || [
                      'We’re building examples for this audience right now.',
                      'Speak with an advisor to walk through your specific situation.',
                    ]).map((item, index) => (
                      <li key={`${activeSegment.id}-placeholder-${index}`} className="flex items-start gap-2">
                        <span className="mt-1 block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={activeSegment.ctaHref || '/contact'}
                    className="inline-flex items-center rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
                  >
                    {activeSegment.placeholder?.ctaText || activeSegment.primaryCTA}
                  </a>
                </div>
              )}

              <div className="space-y-12">
                {filteredStudies.map((study) => {
                  const published = formatDate(study.publishDate);
                  const isExpanded = expandedSlug === study.slug;
                  const primaryResult = study.metrics[0]?.value || 'Custom outcome tailored to the client';
                  const challengeSummary = getFirstSentence(study.challenge);
                  const solutionSummary = getFirstSentence(study.strategy);
                  const primaryCTAUrl = study.cta || activeSegment.ctaHref || '/contact';
                  const primaryCTAText = activeSegment.primaryCTA;

                  return (
                  <article
                    key={study.slug}
                    id={study.slug}
                    className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 md:p-10"
                  >
                    <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-6">
                      <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-1 text-sm font-medium text-blue-700">
                        {study.industry}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1 text-sm font-medium text-slate-600">
                        {study.region}
                      </span>
                      {published && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-4 py-1 text-sm font-medium text-slate-500">
                          Published {published}
                        </span>
                      )}
                    </div>

                    <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-4">{study.title}</h2>

                    <div className="mb-6 grid gap-4 md:grid-cols-3">
                      <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-5">
                        <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">The challenge</p>
                        <p className="text-sm text-blue-900 leading-6">{challengeSummary}</p>
                      </div>
                      <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-5">
                        <p className="text-xs uppercase tracking-wide text-emerald-700 mb-2">Our solution</p>
                        <p className="text-sm text-emerald-900 leading-6">{solutionSummary}</p>
                      </div>
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 p-5">
                        <p className="text-xs uppercase tracking-wide text-indigo-700 mb-2">Net result</p>
                        <p className="text-sm font-semibold text-indigo-900 leading-6">{primaryResult}</p>
                      </div>
                    </div>

                    <p className="text-base md:text-lg text-gray-600 leading-8 mb-8">{study.summary}</p>

                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Client snapshot</h3>
                        <p className="text-sm text-gray-600 leading-6">{study.clientProfile}</p>
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mt-6">The pain</h3>
                        <p className="text-sm text-gray-600 leading-6">{study.challenge}</p>
                      </div>
                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">PCM playbook</h3>
                        <p className="text-sm text-gray-600 leading-6">{study.strategy}</p>
                        {study.metrics.length > 0 && (
                          <div className="mt-6">
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">Measured impact (snapshot)</h3>
                            <ul className="space-y-2">
                              {study.metrics.slice(0, 2).map((metric, index) => (
                                <li key={`${study.slug}-metric-mini-${index}`} className="text-sm text-gray-600 leading-6">
                                  <span className="font-semibold text-gray-800">{metric.label}:</span> {metric.value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {study.metrics.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">Full impact breakdown</h3>
                        <div className="grid gap-4 md:grid-cols-3">
                          {study.metrics.map((metric, index) => (
                            <div
                              key={`${study.slug}-metric-${index}`}
                              className="rounded-xl border border-blue-100 bg-blue-50/60 p-5"
                            >
                              <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">{metric.label}</p>
                              <p className="text-base font-semibold text-blue-900 leading-6">{metric.value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {study.implementationHighlights.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Implementation Highlights</h3>
                        <ul className="list-disc pl-4 space-y-2 text-sm text-gray-600 leading-6">
                          {study.implementationHighlights.map((item, index) => (
                            <li key={`${study.slug}-highlight-${index}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {study.services.length > 0 && (
                      <div className="mt-8 flex flex-wrap gap-2">
                        {study.services.map((service) => (
                          <span
                            key={`${study.slug}-${service}`}
                            className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600"
                          >
                            {formatServiceLabel(service)}
                          </span>
                        ))}
                      </div>
                    )}

                    {study.testimonial && (
                      <blockquote className="mt-8 rounded-xl border-l-4 border-blue-200 bg-blue-50/60 p-4 text-gray-700 italic">
                        “{study.testimonial.quote}”
                        <footer className="mt-3 text-sm font-medium not-italic text-blue-900">
                          — {study.testimonial.attribution}
                        </footer>
                      </blockquote>
                    )}

                    {study.sources && study.sources.length > 0 && (
                      <div className="mt-8">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Sources & Supporting Notes</h3>
                        <ul className="list-disc pl-4 space-y-2 text-xs text-gray-500">
                          {study.sources.map((source, index) => (
                            <li key={`${study.slug}-source-${index}`}>
                              {source.url ? (
                                <a
                                  href={source.url}
                                  className="underline decoration-blue-500 decoration-2 underline-offset-4 hover:text-blue-700"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {source.title}
                                </a>
                              ) : (
                                source.title
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-10 flex flex-wrap gap-4">
                      <a
                        className="inline-flex items-center rounded-lg px-5 py-3 text-sm font-semibold text-white transition-colors"
                        style={{ background: colors.brand.primary }}
                        href={primaryCTAUrl}
                      >
                        {primaryCTAText}
                      </a>
                      <button
                        type="button"
                        onClick={() => setExpandedSlug(isExpanded ? null : study.slug)}
                        className="inline-flex items-center rounded-lg border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-100"
                      >
                        {isExpanded ? 'Hide full narrative' : 'Read the full narrative'}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-6 text-gray-700">
                        <div className="prose max-w-none prose-headings:text-gray-900 prose-p:text-gray-700">
                          <study.Component />
                        </div>
                        {study.compliance && (
                          <p className="mt-6 text-xs text-gray-500">{study.compliance}</p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            </>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
